import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';
import { useRoleCheck } from '@/components/ProtectedRoute';

type UserRow = {
  id_user: number;
  meccanografico: string;
  nome: string;
  cognome: string;
  nome_completo?: string;
  email?: string;
  cellulare?: string;
  qualifica_nome?: string;
  id_sezione?: number;
  sezione_nome?: string;
  status?: number;
  status_nome?: string;
  auth_info?: {
    username?: string;
    role?: string;
    role_display?: string;
    last_login?: string|null;
    is_active?: boolean;
  }
};

// (tipi comodi per i lookup)
type DRFQualifica = { id_qualifica: number; qualifica_name: string };
type DRFSezione  = { id_sezione: number; sezione_name: string };
type DRFStatus   = { id_status: number; status_name: string };


const ROLE_OPTIONS = [
  { value: 'arbitro', label: 'Arbitro' },
  { value: 'organo_tecnico', label: 'Organo Tecnico' },
  { value: 'vice_presidente', label: 'Vice Presidente' },
  { value: 'presidente', label: 'Presidente' },
  { value: 'ref_informatico', label: 'Referente Informatico' },
  { value: 'segretario', label: 'Segretario' },
];



export default function AdminUsersPage() {
  const qc = useQueryClient();
  const { hasAnyRole } = useRoleCheck();
  const canManage = hasAnyRole(['ref_informatico','presidente','vice_presidente','organo_tecnico']);

  // ------------------- state filtri & paging -------------------
  const [page, setPage] = React.useState(1);
const pageSize = 50;
  const [search, setSearch] = React.useState('');
  const [ordering, setOrdering] = React.useState('cognome,nome');
    const [showPwd, setShowPwd] = React.useState(false);
  const [qualificaId, setQualificaId] = React.useState<string>('');
  const [sezioneId, setSezioneId] = React.useState<string>('');
  const [statusId, setStatusId] = React.useState<string>('');
  const [role, setRole] = React.useState<string>('');
  const [isActive, setIsActive] = React.useState<string>(''); // '', 'true', 'false' per accesso auth

const { data: qualif = [] } = useQuery<DRFQualifica[]>({
  queryKey: ['lk-qualifiche'],
  queryFn: async () => {
    const r = await apiClient.getQualifiche();
    const d = r.data as any;
    return Array.isArray(d) ? d : (d?.results ?? []);
  },
  staleTime: 10 * 60_000,
});

const { data: sezioni = [] } = useQuery<DRFSezione[]>({
  queryKey: ['lk-sezioni'],
  queryFn: async () => {
    const r = await apiClient.getSezioni();
    const d = r.data as any;
    return Array.isArray(d) ? d : (d?.results ?? []);
  },
  staleTime: 10 * 60_000,
});

const { data: stati = [] } = useQuery<DRFStatus[]>({
  queryKey: ['lk-status'],
  queryFn: async () => {
    const r = await apiClient.getStatus();
    const d = r.data as any;
    return Array.isArray(d) ? d : (d?.results ?? []);
  },
  staleTime: 10 * 60_000,
});

  // lista utenti (server-side)
  const { data, isFetching } = useQuery({
    queryKey: ['users', page, pageSize, search, ordering, qualificaId, sezioneId, statusId, role, isActive],
    queryFn: async () => {
      const res = await apiClient.getUsers({
        page,
        page_size: pageSize,
        search: search || undefined,
        ordering: ordering || undefined,
        id_qualifica: qualificaId || undefined,
        id_sezione: sezioneId || undefined,
        status: statusId || undefined,
        role: role || undefined,
        is_active: isActive || undefined,
      });
      return res.data;
    },
    keepPreviousData: true,
    staleTime: 60_000,
  });

const rows: UserRow[] = data?.results ?? [];
const count: number = data?.count ?? 0;

  // ------------------- azioni -------------------
  const resetPwd = useMutation({
    mutationFn: (vars: { user_id: number; new_password: string }) => apiClient.adminResetPassword(vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const toggleActive = useMutation({
    mutationFn: (vars: { user_id: number; is_active: boolean }) => apiClient.adminToggleActive(vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const alignAccess = useMutation({
    mutationFn: () => apiClient.adminAlignAccessToStatus(),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      const data = res?.data ?? {};
      alert(
        `Allineamento completato.\n` +
        `Controllati: ${data.checked ?? 0}\n` +
        `Aggiornati: ${data.updated ?? 0}\n` +
        `Attivati: ${data.activated ?? 0}\n` +
        `Disattivati: ${data.deactivated ?? 0}\n` +
        `Senza accesso: ${data.without_auth ?? 0}`
      );
    },
    onError: (e: any) => alert(e?.response?.data?.error || 'Errore durante l\'allineamento'),
  });

  const setRoleMut = useMutation({
    mutationFn: (vars: { user_id: number; role: string }) => apiClient.adminSetUserRole(vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  // modali semplici
  const [pwdModal, setPwdModal] = React.useState<{open:boolean; user?: UserRow}>({open:false});
  const [roleModal, setRoleModal] = React.useState<{open:boolean; user?: UserRow}>({open:false});

  const [newPwd, setNewPwd] = React.useState('');
  const [newPwd2, setNewPwd2] = React.useState('');
  const [newRole, setNewRole] = React.useState('');

  function submitResetPassword() {
    if (!pwdModal.user) return;
    if (!newPwd || newPwd !== newPwd2) return alert('Le password non coincidono.');
    resetPwd.mutate({ user_id: pwdModal.user.id_user, new_password: newPwd }, {
      onSuccess: () => { setPwdModal({open:false}); setNewPwd(''); setNewPwd2(''); },
      onError: (e: any) => alert(e?.response?.data?.error || 'Errore'),
    });
  }

  function submitSetRole() {
    if (!roleModal.user) return;
    if (!newRole) return alert('Seleziona un ruolo.');
    setRoleMut.mutate({ user_id: roleModal.user.id_user, role: newRole }, {
      onSuccess: () => { setRoleModal({open:false}); setNewRole(''); },
      onError: (e: any) => alert(e?.response?.data?.error || 'Errore'),
    });
  }

  return (
    <div className="page-admin-users space-y-4">
      <div className="bg-white shadow rounded-lg p-4">
        <h1 className="text-xl font-semibold mb-3">Gestione Utenti</h1>

        {/* Filtri */}
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-2">
          <input
            className="rounded-md border px-3 py-2 text-sm"
            placeholder="Cerca (nome, cognome, meccanografico, email)"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />

          <select className="rounded-md border px-3 py-2 text-sm" value={qualificaId} onChange={(e)=>{setQualificaId(e.target.value); setPage(1);}}>
            <option value="">Qualifica</option>
            {qualif.map((q:any)=> <option key={q.id_qualifica} value={q.id_qualifica}>{q.qualifica_name}</option>)}
          </select>

          <select className="rounded-md border px-3 py-2 text-sm" value={sezioneId} onChange={(e)=>{setSezioneId(e.target.value); setPage(1);}}>
            <option value="">Sezione</option>
            {sezioni.map((s:any)=> <option key={s.id_sezione} value={s.id_sezione}>{s.sezione_name}</option>)}
          </select>

          <select className="rounded-md border px-3 py-2 text-sm" value={statusId} onChange={(e)=>{setStatusId(e.target.value); setPage(1);}}>
            <option value="">Status</option>
            {stati.map((st:any)=> <option key={st.id_status} value={st.id_status}>{st.status_name}</option>)}
          </select>

          <select className="rounded-md border px-3 py-2 text-sm" value={role} onChange={(e)=>{setRole(e.target.value); setPage(1);}}>
            <option value="">Ruolo</option>
            {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>

          <select className="rounded-md border px-3 py-2 text-sm" value={isActive} onChange={(e)=>{setIsActive(e.target.value); setPage(1);}}>
            <option value="">Accesso</option>
            <option value="true">Accesso attivo</option>
            <option value="false">Accesso disattivo</option>
          </select>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between mt-3">
          <div className="text-sm text-gray-600">
            {isFetching ? 'Caricamento…' : (count !== undefined ? `Totale: ${count}` : '')}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1.5 rounded-md border text-sm hover:bg-gray-50 disabled:opacity-50"
              disabled={!canManage || alignAccess.isLoading}
              onClick={() => {
                if (!canManage) return alert('Permesso negato');
                if (!confirm('Allineare l\'accesso login allo Status anagrafico per tutti gli utenti?')) return;
                alignAccess.mutate();
              }}
            >
              {alignAccess.isLoading ? 'Allineamento...' : 'Allinea status'}
            </button>
            <select className="rounded-md border px-2 py-1 text-sm" value={ordering} onChange={(e)=>{ setOrdering(e.target.value); setPage(1); }}>
              <option value="cognome,nome">Ordina: Cognome ↑</option>
              <option value="-cognome,-nome">Ordina: Cognome ↓</option>
              <option value="data_nascita">Data nascita ↑</option>
              <option value="-data_nascita">Data nascita ↓</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Mecc.</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Nome</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Cognome</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Qualifica</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Sezione</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Email</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Cellulare</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Ruolo</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Accesso</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Last login</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((u) => {
                const roleDisp = u.auth_info?.role_display || '-';
                const hasAuth = !!u.auth_info;
                const active = !!u.auth_info?.is_active;
                const statusName = u.status_nome || '-';
                const isUserStatusActive = statusName.toLowerCase() === 'attivo';
                const lastLogin = u.auth_info?.last_login ? new Date(u.auth_info.last_login).toLocaleString('it-IT') : '—';
                return (
                  <tr key={u.id_user} className="hover:bg-gray-50">
                    <td className="px-3 py-2">{u.meccanografico || '—'}</td>
                    <td className="px-3 py-2">{u.nome}</td>
                    <td className="px-3 py-2">{u.cognome}</td>
                    <td className="px-3 py-2">{u.qualifica_nome || '—'}</td>
                    <td className="px-3 py-2">{u.sezione_nome || '—'}</td>
                    <td className="px-3 py-2">{u.email || '—'}</td>
                    <td className="px-3 py-2">{u.cellulare || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${isUserStatusActive ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                        {statusName}
                      </span>
                    </td>
                    <td className="px-3 py-2">{roleDisp}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${!hasAuth ? 'bg-gray-100 text-gray-600' : active ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                        {!hasAuth ? 'Non sincronizzato' : active ? 'Attivo' : 'Disattivo'}
                      </span>
                    </td>
                    <td className="px-3 py-2">{lastLogin}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        <button
                          className="px-2 py-1 rounded border text-xs hover:bg-gray-50"
                          onClick={() => canManage ? setPwdModal({open:true, user:u}) : alert('Permesso negato')}
                        >
                          Reset password
                        </button>
                        <button
                          className="px-2 py-1 rounded border text-xs hover:bg-gray-50"
                          onClick={() => canManage ? (setNewRole(u.auth_info?.role || ''), setRoleModal({open:true, user:u})) : alert('Permesso negato')}
                        >
                          Assegna ruolo
                        </button>
                        <button
                          className="px-2 py-1 rounded border text-xs hover:bg-gray-50"
                          onClick={() => {
                            if (!canManage) return alert('Permesso negato');
                            toggleActive.mutate({ user_id: u.id_user, is_active: !active }, {
                              onError: (e:any)=>alert(e?.response?.data?.error || 'Errore'),
                            });
                          }}
                        >
                          {active ? 'Disattiva' : 'Attiva'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && !isFetching && (
                <tr><td colSpan={12} className="px-3 py-8 text-center text-gray-500">Nessun utente trovato.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
          <button
            className="px-3 py-1.5 rounded border text-sm disabled:opacity-50"
            onClick={() => setPage((p)=>Math.max(1,p-1))}
            disabled={page<=1 || isFetching}
          >
            ‹‹ Indietro
          </button>
          <div className="text-sm text-gray-600">Pagina {page}</div>
          <button
            className="px-3 py-1.5 rounded border text-sm disabled:opacity-50"
            onClick={() => {
              const total = count ?? (page*pageSize + rows.length);
              const maxPage = Math.ceil(total / pageSize) || 1;
              setPage((p)=>Math.min(maxPage, p+1));
            }}
            disabled={isFetching || (count !== undefined && page*pageSize >= count)}
          >
            Avanti ››
          </button>
        </div>
      </div>

{/* Modal: reset password */}
{pwdModal.open && pwdModal.user && (
  <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-5">
      <h3 className="text-lg font-semibold mb-2">Reset password</h3>
      <p className="text-sm text-gray-600 mb-3">
        {pwdModal.user.nome} {pwdModal.user.cognome} ({pwdModal.user.meccanografico})
      </p>

      <div className="space-y-2">
        <div className="relative">
          <input
            type={showPwd ? 'text' : 'password'}
            className="w-full rounded-md border px-3 py-2 text-sm pr-20"
            placeholder="Nuova password"
            value={newPwd}
            onChange={(e)=>setNewPwd(e.target.value)}
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs px-2 py-1 border rounded"
            onClick={()=>setShowPwd(s => !s)}
          >
            {showPwd ? 'Nascondi' : 'Mostra'}
          </button>
        </div>

        <input
          type={showPwd ? 'text' : 'password'}
          className="w-full rounded-md border px-3 py-2 text-sm"
          placeholder="Conferma password"
          value={newPwd2}
          onChange={(e)=>setNewPwd2(e.target.value)}
        />
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button className="px-3 py-1.5 text-sm" onClick={()=>{ setPwdModal({open:false}); setNewPwd(''); setNewPwd2(''); }}>
          Annulla
        </button>
        <button
          className="px-3 py-1.5 rounded bg-primary-600 text-white text-sm disabled:opacity-50"
          disabled={resetPwd.isLoading || !newPwd || newPwd!==newPwd2}
          onClick={submitResetPassword}
        >
          {resetPwd.isLoading ? 'Salvataggio…' : 'Imposta'}
        </button>
      </div>
    </div>
  </div>
)}


      {/* Modal: ruolo */}
      {roleModal.open && roleModal.user && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-5">
            <h3 className="text-lg font-semibold mb-2">Assegna ruolo</h3>
            <p className="text-sm text-gray-600 mb-3">
              {roleModal.user.nome} {roleModal.user.cognome} — ruolo attuale: <b>{roleModal.user.auth_info?.role_display || '—'}</b>
            </p>
            <select className="w-full rounded-md border px-3 py-2 text-sm" value={newRole} onChange={(e)=>setNewRole(e.target.value)}>
              <option value="">Seleziona ruolo…</option>
              {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <div className="mt-4 flex justify-end gap-2">
              <button className="px-3 py-1.5 text-sm" onClick={()=>{ setRoleModal({open:false}); setNewRole(''); }}>Annulla</button>
              <button className="px-3 py-1.5 rounded bg-primary-600 text-white text-sm disabled:opacity-50"
                      disabled={setRoleMut.isLoading || !newRole}
                      onClick={submitSetRole}>
                {setRoleMut.isLoading ? 'Salvataggio…' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
